import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { procurementService } from '../../../../services/procurementService';
import type {
  ProcurementItemBalanceAdmin,
  UpsertProcurementItemBalanceRequest,
} from '../../../../types/procurement';
import { ItemSearchSelect } from '../../../common/ItemSearchSelect';
import { EditModal, FormField, inputClasses } from './EditModal';

export const ProcurementItemBalancesSection: React.FC = () => {
  const [rows, setRows] = useState<ProcurementItemBalanceAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{
    mode: 'create' | 'edit';
    entity: UpsertProcurementItemBalanceRequest;
    originalKey?: string;
    itemDescription?: string | null;
    replenishmentDescription?: string | null;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    procurementService
      .getAllBalances()
      .then(setRows)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const empty = (): UpsertProcurementItemBalanceRequest => ({
    procurementItemNo: '',
    replenishmentItem: '',
    warehouseBalance: null,
    expectedReceptionDate: null,
    recommendedQuantityReplenishmentItem: null,
    recommendedUnitReplenishmentItem: '',
  });

  const handleSave = async () => {
    if (!editing) return;
    const e = editing.entity;
    if (!e.procurementItemNo.trim()) {
      setError('Varenummer er påkrevd');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editing.mode === 'create') {
        await procurementService.createBalance(e);
      } else if (editing.originalKey) {
        await procurementService.updateBalance(editing.originalKey, e);
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ukjent feil');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: ProcurementItemBalanceAdmin) => {
    if (!window.confirm(`Slette saldo-rad for ${row.procurementItemNo}?`)) return;
    try {
      await procurementService.deleteBalance(row.procurementItemNo);
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Kunne ikke slette');
    }
  };

  const field = <K extends keyof UpsertProcurementItemBalanceRequest>(key: K, value: UpsertProcurementItemBalanceRequest[K]) => {
    if (!editing) return;
    setEditing({ ...editing, entity: { ...editing.entity, [key]: value } });
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('nb-NO');
    } catch {
      return iso;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Saldo sentrallager</h2>
          <p className="text-xs text-gray-500 mt-1">
            Én rad per vare på sentrallageret. Normalt fylt via integrasjon, men kan vedlikeholdes manuelt her.
          </p>
        </div>
        <button
          onClick={() => setEditing({ mode: 'create', entity: empty() })}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-1.5 rounded flex items-center gap-2 text-sm"
        >
          <Plus size={16} /> Ny saldo-rad
        </button>
      </div>

      {error && !editing && (
        <div className="m-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">{error}</div>
      )}

      {loading ? (
        <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={18} /> Laster…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">Ingen saldo-rader registrert.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2 text-left">Varenr</th>
              <th className="px-4 py-2 text-left">Beskrivelse</th>
              <th className="px-4 py-2 text-right">Saldo</th>
              <th className="px-4 py-2 text-left">Ankomst</th>
              <th className="px-4 py-2 text-left">Erstatningsvare</th>
              <th className="px-4 py-2 text-right">Anb. antall</th>
              <th className="px-4 py-2 text-left">Enhet</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{r.procurementItemNo}</td>
                <td className="px-4 py-2 text-gray-700">{r.itemDescription || '—'}</td>
                <td className="px-4 py-2 text-right">{r.warehouseBalance ?? '—'}</td>
                <td className="px-4 py-2 text-gray-700">{formatDate(r.expectedReceptionDate)}</td>
                <td className="px-4 py-2 text-gray-700">
                  {r.replenishmentItem ? (
                    <>
                      <span className="font-medium">{r.replenishmentItem}</span>
                      {r.replenishmentDescription && <span className="text-gray-500"> – {r.replenishmentDescription}</span>}
                    </>
                  ) : '—'}
                </td>
                <td className="px-4 py-2 text-right">{r.recommendedQuantityReplenishmentItem ?? '—'}</td>
                <td className="px-4 py-2 text-gray-700">{r.recommendedUnitReplenishmentItem || '—'}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => setEditing({
                      mode: 'edit',
                      entity: {
                        procurementItemNo: r.procurementItemNo,
                        replenishmentItem: r.replenishmentItem ?? '',
                        warehouseBalance: r.warehouseBalance ?? null,
                        expectedReceptionDate: r.expectedReceptionDate
                          ? r.expectedReceptionDate.substring(0, 10) : null,
                        recommendedQuantityReplenishmentItem: r.recommendedQuantityReplenishmentItem ?? null,
                        recommendedUnitReplenishmentItem: r.recommendedUnitReplenishmentItem ?? '',
                      },
                      originalKey: r.procurementItemNo,
                      itemDescription: r.itemDescription,
                      replenishmentDescription: r.replenishmentDescription,
                    })}
                    className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 mr-1"
                    aria-label="Rediger"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(r)}
                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                    aria-label="Slett"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <EditModal
          title={editing.mode === 'create' ? 'Ny saldo-rad' : `Rediger ${editing.originalKey}`}
          onClose={() => { setEditing(null); setError(null); }}
          onSave={handleSave}
          saving={saving}
          error={error}
          canSave={editing.entity.procurementItemNo.trim().length > 0}
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Varenummer" required>
              {editing.mode === 'edit' ? (
                <input type="text" readOnly value={editing.entity.procurementItemNo}
                  className={`${inputClasses} bg-gray-100`} />
              ) : (
                <ItemSearchSelect
                  value={editing.entity.procurementItemNo}
                  selectedLabel={editing.itemDescription ?? undefined}
                  placeholder="Søk varenummer…"
                  onChange={(itemNo, item) => {
                    if (!editing) return;
                    setEditing({
                      ...editing,
                      entity: { ...editing.entity, procurementItemNo: itemNo },
                      itemDescription: item?.description1 ?? null,
                    });
                  }}
                />
              )}
            </FormField>
            <FormField label="Varenavn (read-only)">
              <input type="text" readOnly value={editing.itemDescription ?? ''}
                className={`${inputClasses} bg-gray-100`} />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Saldo sentrallager">
              <input type="number" step="any"
                value={editing.entity.warehouseBalance ?? ''}
                onChange={(e) => field('warehouseBalance', e.target.value === '' ? null : Number(e.target.value))}
                className={inputClasses} />
            </FormField>
            <FormField label="Forventet ankomstdato">
              <input type="date"
                value={editing.entity.expectedReceptionDate ?? ''}
                onChange={(e) => field('expectedReceptionDate', e.target.value || null)}
                className={inputClasses} />
            </FormField>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Anbefalt erstatningsvare</h4>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Erstatningsvare">
                <ItemSearchSelect
                  value={editing.entity.replenishmentItem ?? ''}
                  selectedLabel={editing.replenishmentDescription ?? undefined}
                  placeholder="Søk erstatningsvare…"
                  onChange={(itemNo, item) => {
                    if (!editing) return;
                    setEditing({
                      ...editing,
                      entity: {
                        ...editing.entity,
                        replenishmentItem: itemNo,
                        recommendedUnitReplenishmentItem:
                          editing.entity.recommendedUnitReplenishmentItem || item?.itemUnitCode || '',
                      },
                      replenishmentDescription: item?.description1 ?? null,
                    });
                  }}
                />
              </FormField>
              <FormField label="Erstatningsvarenavn (read-only)">
                <input type="text" readOnly value={editing.replenishmentDescription ?? ''}
                  className={`${inputClasses} bg-gray-100`} />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <FormField label="Anbefalt antall">
                <input type="number" step="any"
                  value={editing.entity.recommendedQuantityReplenishmentItem ?? ''}
                  onChange={(e) => field('recommendedQuantityReplenishmentItem', e.target.value === '' ? null : Number(e.target.value))}
                  className={inputClasses} />
              </FormField>
              <FormField label="Anbefalt enhet">
                <input type="text" value={editing.entity.recommendedUnitReplenishmentItem ?? ''}
                  onChange={(e) => field('recommendedUnitReplenishmentItem', e.target.value)}
                  className={inputClasses} />
              </FormField>
            </div>
          </div>
        </EditModal>
      )}
    </div>
  );
};
